import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AllowedBoothData,
  AllowedCompanyData,
  AllowedJobData,
  Audience,
  IDateRange,
  Location,
  MatchingSession,
  SessionType,
} from '@data/session/session.model';
import { RecrewtValidators } from '@shared/util/forms.util';
import { UserQuery } from '@data/user/user.query';
import { enumValues } from '@shared/util/enum.util';
import { DEFAULT_DATE_RANGE } from '@core/constants/date';
import { ColumnConfig } from '@shared/components/dynamic-form-table/dynamic-form-table.component';
import { toDotNotationAggregateArraysDeep, toObjectFromDotNotation } from '@core/util/object';
import { SessionJobDatasetService } from '@data/session-job-dataset/session-job-dataset.service';
import { StudyFieldService } from '@data/study-field/study-field.service';
import { switchMap } from 'rxjs/operators';
import { StudyField } from '@data/study-field/study-field.model';
import { forkJoin } from 'rxjs';
import { CustomBranchService } from '@data/custom-branch/custom-branch.service';
import { CustomBranch } from '@data/custom-branch/custom-branch.model';
import { CustomTopicService } from '@data/custom-topic/custom-topic.service';
import { CustomTopic } from '@data/custom-topic/custom-topic.model';

type DatasetValue = {
  availableStudyFields: {
    id?: string;
    identifiableName: { de: string; en: string };
  }[];
};

export interface SessionDialogResponse {
  value: {
    name: string;
    dates: IDateRange[];
    location: Location;
    mode: SessionType;
    locale: 'by' | 'bw' | 'ns' | 'sa';
    image: string | File | null;
    pin?: string;
    allowBoothData?: AllowedBoothData[];
    allowCompanyData?: AllowedCompanyData[];
    allowJobData?: AllowedJobData[];
    jobMatchingEnabled?: boolean;
    recrewtSessionRef?: string;
  };
  datasetValue: DatasetValue;
}

@Component({
  selector: 'recrewt-session-dialog',
  templateUrl: './session-dialog.component.html',
  styleUrls: ['./session-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SessionDialogComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private dialogRef = inject<MatDialogRef<SessionDialogComponent>>(MatDialogRef);
  private userQuery = inject(UserQuery);
  private datasetService = inject(SessionJobDatasetService);
  private studyFieldsService = inject(StudyFieldService);
  private customBranchesService = inject(CustomBranchService);
  private customTopicsService = inject(CustomTopicService);
  private cdr = inject(ChangeDetectorRef);
  data = inject<MatchingSession | null>(MAT_DIALOG_DATA);

  logoUrl: string | null = typeof this.data?.image === 'string' ? this.data?.image : null;

  isAdmin = this.userQuery.isFairsAdmin();

  readonly studyFieldsConfig: ColumnConfig[] = [
    { key: '_id', type: 'hidden' },
    { key: 'identifiableName.de', type: 'string', label: 'Bezeichnung - DE' },
    { key: 'identifiableName.en', type: 'string', label: 'Bezeichnung - EN' },
  ];

  readonly branchConfig: ColumnConfig[] = [
    { key: '_id', type: 'hidden' },
    { key: 'identifiableName.de', type: 'string', label: 'Bezeichnung - DE' },
    { key: 'identifiableName.en', type: 'string', label: 'Bezeichnung - EN' },
  ];

  readonly topicConfig: ColumnConfig[] = [
    { key: '_id', type: 'hidden' },
    { key: 'identifiableName.de', type: 'string', label: 'Bezeichnung - DE' },
    { key: 'identifiableName.en', type: 'string', label: 'Bezeichnung - EN' },
  ];

  readonly AllowedBoothData: AllowedBoothData[] = enumValues(AllowedBoothData, 'string');

  readonly AllowedCompanyData = enumValues(AllowedCompanyData, 'string');

  readonly AllowedJobData = enumValues(AllowedJobData, 'string');

  readonly Audience = enumValues(Audience, 'string');

  readonly EAudience = Audience;

  form = this.initForm();

  datasetForm?: FormGroup;

  get isFormValid(): boolean {
    if (this.form.value.mode === SessionType.FAIR) {
      return this.form.valid;
    } else {
      return this.form.controls.name.valid;
    }
  }

  ngOnInit() {
    if (this.data?.jobDataset) {
      const dataset$ = this.datasetService.getById(this.data.jobDataset);
      dataset$
        .pipe(
          switchMap((it) => {
            const studyFields$ = this.studyFieldsService.get({
              filter: JSON.stringify({
                _id: { $in: it?.availableStudyFields ?? [] },
              }),
            });
            const branches$ = this.customBranchesService.get({
              filter: JSON.stringify({
                _id: { $in: it?.branches ?? [] },
              }),
            });
            const topics$ = this.customTopicsService.get({
              filter: JSON.stringify({
                _id: { $in: it?.topics ?? [] },
              }),
            });

            return forkJoin([studyFields$, branches$, topics$]);
          }),
        )
        .subscribe(([studyFields, branches, topics]) => {
          this.datasetForm = this.initDatasetForm(studyFields ?? [], branches ?? [], topics ?? []);
          this.cdr.detectChanges();
        });
    } else {
      this.datasetForm = this.initDatasetForm([], [], []);
      this.cdr.detectChanges();
    }
  }

  initForm() {
    return this.formBuilder.group({
      name: [this.data?.name, [Validators.required]],
      dates: [
        this.data?.dates?.map((it) => ({
          start: it.start?.slice(0, 16),
          end: it.end?.slice(0, 16),
        })) ?? [DEFAULT_DATE_RANGE],
        [Validators.required],
      ],
      pin: [this.data?.pin],
      mode: [this.data?.mode ?? SessionType.DEFAULT],
      location: [this.data?.location, [RecrewtValidators.fromList()]],
      image: [this.data?.image as string | File | null],
      isProject: [this.data?.isProject],
      locale: [this.data?.locale ?? 'by'],
      allowBoothData: this.formBuilder.array(
        this.getInitialValues(this.AllowedBoothData, this.data?.allowBoothData ?? []),
      ),
      allowCompanyData: this.formBuilder.array(
        this.getInitialValues(this.AllowedCompanyData, this.data?.allowCompanyData ?? []),
      ),
      allowJobData: this.formBuilder.array(
        this.getInitialValues(this.AllowedJobData, this.data?.allowJobData ?? []),
      ),
      audience: this.formBuilder.array(
        this.getInitialValues(this.Audience, this.data?.audience ?? []),
      ),
      jobMatchingEnabled: [this.data?.jobMatchingEnabled ?? false],
      recrewtSessionRef: [this.data?.recrewtSessionRef ?? ''],
    });
  }

  initDatasetForm(fields: StudyField[], branches: CustomBranch[], topics: CustomTopic[]) {
    const v = fields.map(toDotNotationAggregateArraysDeep);
    const b = branches.map(toDotNotationAggregateArraysDeep);
    const t = topics.map(toDotNotationAggregateArraysDeep);
    return this.formBuilder.group({
      availableStudyFields: [v],
      branches: [b],
      topics: [t],
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.isFormValid) {
      const value = this.form.value;
      const datasetValue: any = this.datasetForm!.value;
      value.allowBoothData = value.allowBoothData?.filter(Boolean);
      value.allowCompanyData = value.allowCompanyData?.filter(Boolean);
      value.allowJobData = value.allowJobData?.filter(Boolean);
      value.audience = value.audience?.filter(Boolean);
      // ponytail: an empty field never clears the target; a sync may have stored one since this dialog's data was loaded
      value.recrewtSessionRef = value.recrewtSessionRef?.trim() || undefined;
      if (datasetValue.availableStudyFields) {
        datasetValue.availableStudyFields =
          datasetValue.availableStudyFields.map(toObjectFromDotNotation);
      }
      if (datasetValue.branches) {
        datasetValue.branches = datasetValue.branches.map(toObjectFromDotNotation);
      }
      if (datasetValue.topics) {
        datasetValue.topics = datasetValue.topics.map(toObjectFromDotNotation);
      }
      this.dialogRef.close({ value, datasetValue });
    }
  }

  selectLogo(file: File): void {
    this.form.controls.image.setValue(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      this.logoUrl = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  deleteLogo() {
    this.logoUrl = null;
    this.form.controls.image.setValue(null);
  }

  private getInitialValues<T>(all: T[], selected: T[]) {
    return all.map((it) => (selected.includes(it) ? it : null));
  }
}
